import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getAnalyticMotionData as getAnalyticMotionDataService,
  getAnalyticLightOPStatusData as getAnalyticLightOPStatusDataService,
} from "src/core/api/services/analytics.service";
import {
  getFloorAssetsAndShapes,
  getFloorDetails,
  getFloorPlansByGateway,
} from "src/core/api/services/dashbaorad-details/floor/floor.service";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import { dataUrlToFile } from "src/shared/helpers/data-url-to-file";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { FloorShape } from "src/shared/models/floorPlan";
import type { ZoneAsset } from "src/modules/asset/components/asset-detail/asset-configuration/components/types";
import { AppDispatch, RootState } from "src/store";
import {
  getAnalyticDaylightData,
  getAnalyticMotionData,
  getAnalyticLightOPStatusData,
} from "src/store/slices/analytics.slice";

import gatewayInitial from "../canvas/images/gateway-initial-filled.png";
import photocellOffline from "../canvas/images/PhotocellSensorGrey.png";

import { DynamicValuesEnum } from "src/core/enums/dynamic-values.enum";
import {
  AnalyticsChartData,
  CanvasDimensions,
  DynamicValues,
  FloorAnalyticsView,
  AnimationPlayerTitle,
  HoverState,
  LabelDynamics,
  PlayerState,
  UIState,
} from "src/core/enums/floor-analytics-enums";
import { calculateAllDynamicValues } from "src/shared/helpers/dynamic-values.helper";
import {
  calculateImageDimensions,
  createPinchZoomHandlers,
  getZoomStepIn,
  getZoomStepOut,
  handleShapeMouseEnter,
  handleShapeMouseLeave,
  handleStageWheel as handleStageWheelHelper,
} from "src/shared/helpers/floorplan.helpers";
import { calculateLabelLayouts } from "src/shared/helpers/label-layout.helper";
import circleSelectedLight from "../canvas/images/circle-light-selected.png";
import "./analytics.scss";
import AnalyticsHeaderComponent from "./components/analytics-header/analytics-header.component";
import CanvasControlsComponent from "./components/canvas-controls/canvas-controls.component";
import AnalyticsChartComponent from "./components/charts/analytics-chart.component";
import FloorplanCanvasComponent from "./components/floorplan-canvas/floorplan-canvas.component";
import HeatmapLegendComponent from "./components/heatmap-legend/heatmap-legend.component";
import HeatmapPlayerComponent from "./components/heatmap-player/heatmap-player.component";
import HoverTooltipComponent from "./components/hover-tooltip/hover-tooltip.component";
import StickyNoteComponent from "./components/sticky-note/sticky-note.component";
import LightOpStatusLegendComponent from "./components/light-op-status-legend/light-op-status-legend.component";
import {
  AnalyticsChartDataTypes,
  floorplanColors,
} from "src/core/enums/floorplan-color-enums";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  isZoneSelectableAssetType,
  ZoneFloorplanSelection,
} from "src/modules/asset/components/asset-detail/asset-configuration/zones/components/zone-floorplan-selection.component";

type AnalyticsComponentProps = {
  zoneSelectionMode?: boolean;
};

const AnalyticsComponent = ({
  zoneSelectionMode = false,
}: AnalyticsComponentProps) => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const floor = params?.floor ?? params?.floorCode;
  const blockCode = params?.blockCode ? safeAtob(params?.blockCode) : null;
  const gatewayCode = new URLSearchParams(location.search).get("gatewayCode");
  const selectionState = (location.state || {}) as any;

  // Redux
  const dispatch = useDispatch<AppDispatch>();

  const {
    motionData,
    isLoadingMotionVsTimeAnalytics,
    daylightData,
    isLoadingDaylightAnalytics,
    lightOPStatusData,
    isLoadingLightOPStatusAnalytics,
  } = useSelector((state: RootState) => state.analytics);

  const selectedAssetCode = useSelector(
    (state: any) => state.asset.selectedAssetCode,
  );

  // Refs
  const stageRef = useRef<any>(null);
  const pinchStateRef = useRef<any>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  // Hover timer to show hover tooltip only after a short delay
  const hoverTimeoutRef = useRef<number | null>(null);
  const HOVER_DELAY = 500;

  // Fullscreen
  const handle = useFullScreenHandle();

  // Main states
  const [date, setDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [selectedViewType, setSelectedViewType] = useState<FloorAnalyticsView>(
    FloorAnalyticsView.ANALYTICS_VIEW,
  );
  const [playerTitle, setPlayerTitle] = useState<AnimationPlayerTitle>(
    AnimationPlayerTitle.HEATMAP,
  );
  const [selectedAssetList, setSelectedAssetList] = useState<string[]>(
    () => zoneSelectionMode
      ? []
      : selectionState.assignedAssets?.map(
        (asset: ZoneAsset) => asset.assetCode,
      ) || [],
  );

  const selectedAssetOrder = useMemo(() => {
    return selectedAssetList.reduce<Record<string, number>>(
      (acc, assetCode, index) => {
        acc[assetCode] = index + 1;
        return acc;
      },
      {},
    );
  }, [selectedAssetList]);

  const [isSelectedAssetListVisible, setIsSelectedAssetListVisible] =
    useState<boolean>(true);
  const [showChartOnCompare, setShowChartOnCompare] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>("LightOPStatus");
  const [render, setRender] = useState<number>(0);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [preventLabelOverlap, setPreventLabelOverlap] =
    useState<boolean>(false);
  const [showLayerDropdown, setShowLayerDropdown] = useState<boolean>(false);
  const [
    selectedSearchedAssetCodeForHighlighting,
    setSelectedSearchedAssetCodeForHighlighting,
  ] = useState<string>("");

  // Canvas state
  const [containerDimensions, setContainerDimensions] =
    useState<CanvasDimensions>({
      width: 942,
      height: 530,
    });

  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [newValueOnStageMove, setNewValueOnStageMove] = useState({
    x: 0,
    y: 0,
  });

  // UI state
  const [uiState, setUiState] = useState<UIState>({
    hoverState: { visible: false, x: 0, y: 0 },
    hovered: false,
    selectedWhileHovered: null,
    stickyNotePosition: { x: window.innerWidth - 410, y: 20 },
  });

  // Player state
  const [playerState, setPlayerState] = useState<PlayerState>({
    hourRange: 0,
    isPlaying: false,
    playbackSpeed: 1,
    showPlayerControls: false,
    playerPosition: { x: window.innerWidth - 550, y: 20 },
    playbackInterval: null,
  });

  // Maximum hour range for the player (used to loop back to start when reached)
  const [maxHourRange, setMaxHourRange] = useState<number>(95);

  // Data state
  const [shapes, setShapes] = useState<FloorShape[]>([]);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isProcessingImage, setIsProcessingImage] = useState<boolean>(true);

  // Heatmap motion data map: assetId -> motiondata array
  const [heatmapMotionDataMap, setHeatmapMotionDataMap] = useState<
    Map<string, (number | null)[]>
  >(new Map());

  // Light OP Status data map: assetId -> lightOPStatusViewData array
  const [lightOPStatusDataMap, setLightOPStatusDataMap] = useState<
    Map<string, (number | null)[]>
  >(new Map());
  const [lightIntensityDataMap, setLightIntensityDataMap] = useState<
    Map<string, (number | null)[]>
  >(new Map());

  // Heatmap query params to trigger the useQuery
  const [heatmapQueryParams, setHeatmapQueryParams] = useState<{
    assetCodes: string[];
    date: string;
  } | null>(null);

  // Light OP Status query params to trigger the useQuery
  const [lightOPStatusQueryParams, setLightOPStatusQueryParams] = useState<{
    assetCodes: string[];
    date: string;
  } | null>(null);

  // Label dynamics object containing all label-related properties
  const initialOverrideDefaultSizeValues = {
    circle: 0,
    circlePositionOffset: 0,
    labelDynamics: {
      fontSize: 0,
      height: 0,
      yOffset: 0,
      padding: 0,
      textPadding: 0,
      strokeWidth: 0,
      cornerRadius: 0,
      shadowBlur: 0,
      shadowColor: 0,
      shadowOffset: 0,
    },
    gateway: {
      size: 0,
      offset: 0,
      labelOffset: 0,
    },
    photocell: {
      size: 0,
      offset: 0,
      labelOffset: 0,
    },
  };

  // This value will be added / subtracted (positive/negative) to the base sizes to override them
  // example: if base circle size = 20, and overrideDefaultSizeValues.circle = 2, the effective base size becomes 22
  const [overrideDefaultSizeValues, setOverrideDefaultSizeValues] = useState(
    initialOverrideDefaultSizeValues,
  );

  // Label dynamics object containing all label-related properties
  const [labelDynamics, setLabelDynamics] = useState<LabelDynamics>({
    fontSize: DynamicValuesEnum.FONT_SIZE,
    height: DynamicValuesEnum.LABEL_HEIGHT,
    yOffset: DynamicValuesEnum.LABEL_Y_OFFSET,
    padding: DynamicValuesEnum.LABEL_PADDING,
    textPadding: DynamicValuesEnum.TEXT_PADDING,
    strokeWidth: DynamicValuesEnum.STROKE_WIDTH,
    cornerRadius: DynamicValuesEnum.CORNER_RADIUS,
    shadowBlur: DynamicValuesEnum.SHADOW_BLUR,
    shadowColor: DynamicValuesEnum.SHADOW_COLOR,
    shadowOffset: DynamicValuesEnum.SHADOW_OFFSET,
  });

  const [dynamicValues, setDynamicValues] = useState<DynamicValues>({
    circle: DynamicValuesEnum.CIRCLE,
    circlePositionOffset: 0,
    labelDynamics,
    gateway: {
      size: DynamicValuesEnum.GATEWAY_SIZE,
      offset: 0,
      labelOffset: DynamicValuesEnum.GATEWAY_LABEL_OFFSET,
    },
    photocell: {
      size: DynamicValuesEnum.PHOTOCELL_SIZE,
      offset: 0,
      labelOffset: DynamicValuesEnum.PHOTOCELL_LABEL_OFFSET,
    },
  });

  const {
    data: floorDetails,
    isLoading: isLoadingFloorDetails,
    error: floorDetailsError,
  } = useQuery({
    queryKey: ["getFloorDetails", blockCode, floor],
    queryFn: ({ signal }) => getFloorDetails(blockCode, floor, signal),
    enabled: !!blockCode && !!floor,
    staleTime: 0, // data becomes stale immediately
    gcTime: 0, // optional: remove cached data asap (if not needed)
    refetchOnMount: "always", // always refetch when component mounts
    refetchOnReconnect: "always",
  });

  let { data: floorAssetsAndShapes, isLoading: isLoadingFloorAssetsAndShapes } =
    useQuery({
      queryKey: ["getFloorAssetsAndShapes", blockCode, floor, gatewayCode],
      queryFn: ({ signal }) =>
        gatewayCode
          ? getFloorPlansByGateway(blockCode, floor, gatewayCode, signal)
          : getFloorAssetsAndShapes(blockCode, floor, signal),
      enabled: !!blockCode && !!floor,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
    });

  // useQuery for fetching heatmap motion data
  const { data: heatmapMotionData, isLoading: isLoadingHeatmapMotionData } =
    useQuery({
      queryKey: [
        "getAnalyticMotionData",
        heatmapQueryParams?.assetCodes,
        heatmapQueryParams?.date,
      ],
      queryFn: ({ signal }) =>
        getAnalyticMotionDataService(
          {
            assetCodes: heatmapQueryParams?.assetCodes || [],
            date: heatmapQueryParams?.date || "",
            chartType: AnalyticsChartDataTypes.Animation,
          },
          signal,
        ),
      enabled:
        !!heatmapQueryParams?.assetCodes?.length && !!heatmapQueryParams?.date,
    });

  // useQuery for fetching light OP status data
  const {
    data: lightOPStatusViewData,
    isLoading: isLoadingLightOPStatusViewData,
  } = useQuery({
    queryKey: [
      "getAnalyticLightOPStatusData",
      lightOPStatusQueryParams?.assetCodes,
      lightOPStatusQueryParams?.date,
    ],
    queryFn: ({ signal }) =>
      getAnalyticLightOPStatusDataService(
        {
          assetCodes: lightOPStatusQueryParams?.assetCodes || [],
          date: lightOPStatusQueryParams?.date || "",
          chartType: AnalyticsChartDataTypes.Animation,
        },
        signal,
      ),
    enabled:
      !!lightOPStatusQueryParams?.assetCodes?.length &&
      !!lightOPStatusQueryParams?.date,
  });

  // Transform API response to chart format
  const motionAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!motionData?.data?.length) return [];
    return motionData.data.map((item: any) => ({
      assetId: item.assetId,
      name: selectedAssetOrder[item.assetId]
        ? `${selectedAssetOrder[item.assetId]} - ${item.displayname}`
        : item.displayname,
      data: item.motiondata,
    }));
  }, [motionData, selectedAssetOrder]);

  // Transform daylight API response to chart format
  const daylightAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!daylightData?.data?.length) return [];
    return daylightData.data.map((item: any) => ({
      assetId: item.assetId,
      name: selectedAssetOrder[item.assetId]
        ? `${selectedAssetOrder[item.assetId]} - ${item.displayname}`
        : item.displayname,
      data: item.daylightdata,
    }));
  }, [daylightData, selectedAssetOrder]);

  // Transform light OP status API response to chart format
  const lightOPStatusAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!lightOPStatusData?.data?.length) return [];

    return lightOPStatusData?.data?.map((item: any) => ({
      assetId: item?.assetId,
      name: selectedAssetOrder[item?.assetId]
        ? `${selectedAssetOrder[item?.assetId]} - ${item?.displayname}`
        : item?.displayname,
      data: item?.lightOPStatus,
      lightIntensity: item?.lightIntensity,
    }));
  }, [lightOPStatusData, selectedAssetOrder]);

  const { assetScale } = useMemo(
    () => calculateImageDimensions(selectedFile),
    [selectedFile],
  );

  const scaledShapes = useMemo(() => {
    if (!shapes?.length) return [];

    return shapes?.map((shape: FloorShape) => ({
      ...shape,
      x: (shape?.x ?? 0) * assetScale.x,
      y: (shape?.y ?? 0) * assetScale.y,
    }));
  }, [shapes, assetScale]);

  // Pre-calculated, non-overlapping label layouts keyed by shape id
  const labelLayouts = useMemo(() => {
    return calculateLabelLayouts({
      showLabels,
      showAssets: true,
      shapes: scaledShapes,
      labelDynamics,
      preventLabelOverlap,
      dynamicCircle: dynamicValues.circle,
      overrideDefaultSizeValue: overrideDefaultSizeValues,
    });
  }, [
    showLabels,
    scaledShapes,
    labelDynamics,
    preventLabelOverlap,
    dynamicValues.circle,
    overrideDefaultSizeValues,
  ]);

  // Color interpolation helper for heatmap
  const getHeatmapColor = useMemo(() => {
    // Color stops from the color axis configuration

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
          r: Number.parseInt(result[1], 16),
          g: Number.parseInt(result[2], 16),
          b: Number.parseInt(result[3], 16),
        }
        : { r: 0, g: 0, b: 0 };
    };

    const rgbToHex = (r: number, g: number, b: number) => {
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = Math.round(x).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    };

    return (
      motionOrIntensityCount: number | null,
      viewType: string = FloorAnalyticsView.PLAYBACK_VIEW_MOTION,
      maxValue: number = 200,
      lightStatusValue: number | null = 1,
    ): string => {
      let colorStops;

      if (viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION) {
        //blue color stops
        colorStops = [
          { stop: 0, color: "#99ccff" },
          { stop: 0.0001, color: "#66b2ff" },
          { stop: 0.33, color: "#3385cc" },
          { stop: 0.66, color: "#0A2F80" },
          { stop: 1, color: "#082160" },
        ];
      } else {
        // Green color stops (same gradient pattern as motion)
        colorStops = [
          { stop: 0, color: "#99ffcc" },
          { stop: 0.0001, color: "#66ff99" },
          { stop: 0.33, color: "#33cc66" },
          { stop: 0.66, color: "#0A802F" },
          { stop: 1, color: "#086021" },
        ];
      }

      // Missing / null data handling:
      // - Heatmap: null/undefined stays gray and solid; 0 stays the same gray color but is rendered as a gradient
      // - Light OP Status:
      //   - missing status => treat as "Off" (gray)
      //   - status=0 => "Off" (gray)
      //   - status=On with missing intensity => default to green
      if (viewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS) {
        if (lightStatusValue === null) return floorplanColors.lightOff;
        if (lightStatusValue === 0) return floorplanColors.lightOff;

        if (
          motionOrIntensityCount === null ||
          motionOrIntensityCount === undefined
        ) {
          return floorplanColors.lightOn;
        }
      } else {
        if (
          motionOrIntensityCount === null ||
          motionOrIntensityCount === undefined ||
          motionOrIntensityCount === 0
        ) {
          return floorplanColors.lightOff;
        }
      }

      // Normalize the value between 0 and 1
      const normalizedValue = Math.min(
        Math.max(motionOrIntensityCount / maxValue, 0),
        1,
      );

      // Find the two stops to interpolate between
      let lowerStop = colorStops[0];
      let upperStop = colorStops[colorStops?.length - 1];

      for (let i = 0; i < colorStops.length - 1; i++) {
        if (
          normalizedValue >= colorStops[i].stop &&
          normalizedValue <= colorStops[i + 1].stop
        ) {
          lowerStop = colorStops[i];
          upperStop = colorStops[i + 1];
          break;
        }
      }

      // Calculate interpolation factor
      const range = upperStop.stop - lowerStop.stop;
      const factor =
        range === 0 ? 0 : (normalizedValue - lowerStop.stop) / range;

      // Interpolate colors
      const lowerRgb = hexToRgb(lowerStop.color);
      const upperRgb = hexToRgb(upperStop.color);

      const r = lowerRgb.r + factor * (upperRgb.r - lowerRgb.r);
      const g = lowerRgb.g + factor * (upperRgb.g - lowerRgb.g);
      const b = lowerRgb.b + factor * (upperRgb.b - lowerRgb.b);

      return rgbToHex(r, g, b);
    };
  }, []);

  /*   // Color interpolation helper for light OP status
  const getLightOPStatusColor = useMemo(() => {
    return (lightOPStatus: number | null): string => {
      // Define colors for different light OP status values
      switch (lightOPStatus) {
        case 0:
          return floorplanColors.lightOff; // Gray for status 0 (light off)
        case 1:
          return floorplanColors.lightOn; // Green for status 1 (light on)
        default:
          return floorplanColors.lightOff; // Gray for null/undefined or other values
      }
    };
  }, []); */

  // Helper function to trigger heatmap data fetch
  const triggerHeatmapDataFetch = () => {
    if (scaledShapes?.length > 0) {
      const allAssetCodes = scaledShapes
        .filter(
          (shape: FloorShape) =>
            shape?.assetCode &&
            shape?.assetType?.toLowerCase() !== "gateway" &&
            shape?.assetType?.toLowerCase() !== "photocell sensor",
        )
        .map((shape: FloorShape) => shape.assetCode);

      if (allAssetCodes.length > 0) {
        setHeatmapQueryParams({
          assetCodes: allAssetCodes,
          date: date,
        });
      }
    }
  };

  // Helper function to trigger light OP status data fetch
  const triggerLightOPStatusDataFetch = () => {
    if (scaledShapes?.length > 0) {
      const allAssetCodes = scaledShapes
        .filter(
          (shape: FloorShape) =>
            shape?.assetCode &&
            shape?.assetType?.toLowerCase() !== "gateway" &&
            shape?.assetType?.toLowerCase() !== "photocell sensor",
        )
        .map((shape: FloorShape) => shape.assetCode);

      if (allAssetCodes.length > 0) {
        setLightOPStatusQueryParams({
          assetCodes: allAssetCodes,
          date: date,
        });
      }
    }
  };

  // Effects
  useEffect(() => {
    // bump render when active tab changes so tabs re-render similar to energy chart
    setRender((r) => r + 1);
  }, [activeKey]);

  useEffect(() => {
    setSelectedFile(null);
    setShapes([]);
  }, [blockCode, floor]);

  useEffect(() => {
    if (floorDetails?.data) {
      setIsProcessingImage(true);
      const base64file = floorDetails?.data?.floorImage;

      if (!base64file) {
        setIsProcessingImage(false);
        return;
      }

      const isBase64 =
        typeof base64file === "string" && base64file?.startsWith("iVBOR");
      const src = isBase64 ? `data:image/png;base64,${base64file}` : base64file;

      const imageElement = new window.Image();
      imageElement.src = src;
      imageElement.onload = () => setSelectedFile(imageElement);
      setTimeout(() => {
        setIsProcessingImage(false);
      }, 300);
    }

    if (floorDetailsError) {
      setIsProcessingImage(false);
    }
  }, [floorDetails, floorDetailsError]);

  useEffect(() => {
    if (isLoadingFloorDetails) {
      setIsProcessingImage(true);
    }
  }, [isLoadingFloorDetails]);

  useEffect(() => {
    if (floorAssetsAndShapes?.data) {
      let row = 0;
      let col = 0;
      const itemsPerRow = 43;
      const yStep = 20;
      const xStep = 20;

      const updatedShapes: FloorShape[] = floorAssetsAndShapes.data.map(
        (item: FloorShape) => {
          const x = item?.x ? item.x : col * xStep + xStep;
          const y = item?.y ? item.y : row * yStep;

          col++;
          if (col >= itemsPerRow) {
            col = 0;
            row++;
          }

          return {
            ...item,
            id: item?.assetCode,
            x,
            y,
            highlightedAsset:
              item?.assetCode === selectedSearchedAssetCodeForHighlighting,
          };
        },
      );

      setShapes(updatedShapes);
    }
  }, [floorAssetsAndShapes, selectedSearchedAssetCodeForHighlighting]);

  useEffect(() => {
    const dimensions = () => {
      const totalWidthElement = document.getElementById("totalWidth");
      setContainerDimensions({
        height: 530,
        width: totalWidthElement?.offsetWidth
          ? totalWidthElement?.offsetWidth - 100
          : 942,
      });
    };

    dimensions();
  }, []);

  useEffect(() => {
    const dynamicValues = calculateAllDynamicValues(
      zoomLevel,
      overrideDefaultSizeValues,
    );
    setDynamicValues(dynamicValues);
    setLabelDynamics(dynamicValues.labelDynamics);
  }, [zoomLevel, overrideDefaultSizeValues]);

  // useEffect(() => {
  //   // Calculate all dynamic values using the helper function
  //   const dynamicValues = calculateAllDynamicValues(
  //     zoomLevel,
  //     overrideDefaultSizeValues
  //   );

  //   // Update all state values with calculated values
  //   setDynamicCircle(dynamicValues.circle);
  //   setCirclePositionOffset(dynamicValues.circlePositionOffset);
  //   setLabelDynamics(dynamicValues.labelDynamics);
  //   setDynamicGateway(dynamicValues.gateway.size);
  //   setDynamicGatewayOffset(dynamicValues.gateway.offset);
  //   setDynamicGatewayLabelOffset(dynamicValues.gateway.labelOffset);
  // }, [zoomLevel, overrideDefaultSizeValues]);

  // Update heatmapMotionDataMap when API response arrives
  useEffect(() => {
    if (heatmapMotionData?.data) {
      const dataMap = new Map<string, (number | null)[]>();
      heatmapMotionData.data.forEach((item: any) => {
        dataMap.set(item.assetId, item.motiondata);
      });

      let tempHourRange = 0;
      heatmapMotionData?.data?.forEach((item: any) => {
        tempHourRange = Math.max(tempHourRange, item.motiondata.length || 0);
      });
      setMaxHourRange(Math.min(95, Math.max(tempHourRange, 0)));
      setHeatmapMotionDataMap(dataMap);
    }
  }, [heatmapMotionData]);

  // Update lightOPStatusDataMap when API response arrives
  useEffect(() => {
    if (lightOPStatusViewData?.data) {
      const lightOPStatusMap = new Map<string, (number | null)[]>();
      const lightIntensityMap = new Map<string, (number | null)[]>();

      lightOPStatusViewData.data.forEach((item: any) => {
        lightOPStatusMap.set(item.assetId, item.lightOPStatus);
        lightIntensityMap.set(item.assetId, item.lightIntensity);
      });

      let tempHourRange = 0;
      lightOPStatusViewData?.data?.forEach((item: any) => {
        tempHourRange = Math.max(tempHourRange, item.lightOPStatus.length || 0);
      });
      setMaxHourRange(Math.min(95, Math.max(tempHourRange, 0)));

      setLightOPStatusDataMap(lightOPStatusMap);
      setLightIntensityDataMap(lightIntensityMap);
    }
  }, [lightOPStatusViewData]);

  // Set zoom level based on view type
  useEffect(() => {
    if (selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION) {
      setZoomLevel(1.5);
    } else {
      setZoomLevel(1);
    }
  }, [selectedViewType, heatmapMotionData, isProcessingImage]);

  // Watch for shapes to load when in heatmap view - handles case when shapes were empty on view switch
  useEffect(() => {
    if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
      scaledShapes?.length > 0 &&
      !heatmapQueryParams
    ) {
      triggerHeatmapDataFetch();
    } else if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS &&
      scaledShapes?.length > 0 &&
      !lightOPStatusQueryParams
    ) {
      triggerLightOPStatusDataFetch();
    }
  }, [scaledShapes, selectedViewType]);

  useEffect(() => {
    if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
      heatmapQueryParams
    ) {
      setHeatmapQueryParams((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          date: moment(date).format("YYYY-MM-DD"),
        };
      });
    } else if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS &&
      lightOPStatusQueryParams
    ) {
      setLightOPStatusQueryParams((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          date: moment(date).format("YYYY-MM-DD"),
        };
      });
    }
  }, [date, selectedViewType]);

  useEffect(() => {
    if (selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION) {
      setPlayerTitle(AnimationPlayerTitle.HEATMAP);
    } else if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS
    ) {
      setPlayerTitle(AnimationPlayerTitle.LIGHT_OP_STATUS);
    }
  }, [selectedViewType]);

  useEffect(() => {
    if (selectedAssetList?.length === 0) {
      setShowChartOnCompare(false);
    }
  }, [selectedAssetList]);

  // Event handlers
  const handleDateChange = (selectedDate: string) => {
    setDate(selectedDate);
    setHeatmapQueryParams((prev: any) => {
      return { ...prev, date: selectedDate };
    });
    setLightOPStatusQueryParams((prev: any) => {
      return { ...prev, date: selectedDate };
    });
  };

  const hasSelectedAssetsBeenSelected = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const assetCodeParam = urlParams.get("assetCode");
    const assetCode = assetCodeParam ? safeAtob(assetCodeParam) : null;

    if (
      !hasSelectedAssetsBeenSelected.current &&
      !zoneSelectionMode &&
      assetCode &&
      selectedAssetCode &&
      assetCode === selectedAssetCode
    ) {
      hasSelectedAssetsBeenSelected.current = true;
      // Add a small delay to ensure the floor plan is ready for highlighting
      setTimeout(() => {
        // highlight the asset on the floor plan (decoded from base64)
        setSelectedAssetList([assetCode]);
      }, 500);
    }
  }, [location.search, selectedAssetCode, zoneSelectionMode]);

  const onChangeViewType = (value: FloorAnalyticsView) => {
    setMaxHourRange(95);
    setSelectedViewType(value);
    setPlayerState((prev) => ({ ...prev, hourRange: 0 }));
    setSelectedAssetList([]);
    setShowChartOnCompare(false);
    setPlayerState((prev) => ({
      ...prev,
      playbackSpeed: 1,
      showPlayerControls: false,
      playerPosition: { x: window.innerWidth - 550, y: 20 },
    }));
    handleStop();
    setDate(moment().format("YYYY-MM-DD"));

    if (value === FloorAnalyticsView.ANALYTICS_VIEW) {
      // Clear heatmap data when switching back to analytics view
      setHeatmapMotionDataMap(new Map());
      setHeatmapQueryParams(null);

      // Clear light OP status data when switching back to analytics view
      setLightOPStatusDataMap(new Map());
      setLightIntensityDataMap(new Map());
      setLightOPStatusQueryParams(null);

      setOverrideDefaultSizeValues(initialOverrideDefaultSizeValues);
    } else if (value === FloorAnalyticsView.PLAYBACK_VIEW_MOTION) {
      // Clear light OP status data when switching to heatmap view
      setLightOPStatusDataMap(new Map());
      setLightIntensityDataMap(new Map());
      setLightOPStatusQueryParams(null);

      setOverrideDefaultSizeValues((prev) => ({
        ...prev,
        circle: 20,
      }));

      triggerHeatmapDataFetch();
    } else if (value === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS) {
      // Clear heatmap data when switching to light OP status view
      setHeatmapMotionDataMap(new Map());
      setHeatmapQueryParams(null);

      setOverrideDefaultSizeValues((prev) => ({
        ...prev,
        circle: 0,
      }));

      triggerLightOPStatusDataFetch();
    }
  };

  // Wheel zoom while hovering the stage — delegating to shared helper
  const handleStageWheel = (e: any) => {
    handleStageWheelHelper({
      e,
      stageRef,
      zoomLevel,
      setZoomLevel,
      newValueOnStageMove,
      setNewValueOnStageMove,
      setTooltip: null,
      tooltip: null,
      setHoverState: (state: HoverState) =>
        setUiState((prev) => ({ ...prev, hoverState: state })),
      hoverState: uiState.hoverState,
      setGlobalSelectedShape: null,
      setSelectedWhileHovered: (shape: FloorShape | null) =>
        setUiState((prev) => ({ ...prev, selectedWhileHovered: shape })),
      dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
    });
  };

  // Touch handlers for smooth pinch-to-zoom / two-finger pan on mobile
  // Touch handlers for smooth pinch-to-zoom / two-finger pan on mobile
  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
    createPinchZoomHandlers(
      {
        stageRef,
        zoomLevel,
        setZoomLevel,
        dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
        dynamicValuesEnumMinZoom: 0.5,
        setNewValueOnStageMove,
      },
      pinchStateRef,
    );

  const handleStageDrag = (x: number, y: number) => {
    setNewValueOnStageMove({ x, y });
  };

  const handleOnAssetClick = (id: string, e?: any) => {
    if (
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION ||
      selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS
    )
      return;

    const selectedShape = scaledShapes.find(
      (shape: FloorShape) => shape.id === id,
    );

    const assetType = selectedShape?.assetType?.toLowerCase();
    if (
      !selectedShape ||
      (zoneSelectionMode
        ? !isZoneSelectableAssetType(assetType)
        : assetType === "gateway")
    )
      return;

    setSelectedAssetList((prev: string[]) => {
      if (prev.includes(selectedShape?.assetCode)) {
        return prev.filter((code) => code !== selectedShape.assetCode);
      }

      return [...prev, selectedShape.assetCode];
    });
  };

  const onAssetMouseEnter = (e: any, shape: FloorShape) => {
    handleShapeMouseEnter({
      e,
      shape,
      shapes: scaledShapes,
      hoverTimeoutRef,
      HOVER_DELAY,
      setHovered: (hovered: boolean) =>
        setUiState((prev) => ({ ...prev, hovered })),
      setSelectedWhileHovered: (selected: FloorShape | null) =>
        setUiState((prev) => ({ ...prev, selectedWhileHovered: selected })),
      setHoverState: (state: HoverState) =>
        setUiState((prev) => ({ ...prev, hoverState: state })),
    });
  };

  const onAssetMouseLeave = (e: any) => {
    handleShapeMouseLeave({
      e,
      hoverTimeoutRef,
      setHovered: (hovered: boolean) =>
        setUiState((prev) => ({ ...prev, hovered })),
      setHoverState: (state: HoverState) =>
        setUiState((prev) => ({ ...prev, hoverState: state })),
      setSelectedWhileHovered: (selected: FloorShape | null) =>
        setUiState((prev) => ({ ...prev, selectedWhileHovered: selected })),
    });
  };

  const handleZoomIn = () => {
    setZoomLevel(
      Math.min(
        DynamicValuesEnum.MAX_ZOOM,
        zoomLevel + getZoomStepIn(zoomLevel),
      ),
    );
    setHoverState({ ...hoverState, visible: false });
  };

  const handleZoomOut = () => {
    setZoomLevel(Math.max(0.5, zoomLevel - getZoomStepOut(zoomLevel)));
    setHoverState({ ...hoverState, visible: false });
  };

  // Reset zoom and re-center the stage layer to top-left
  const handleResetZoom = () => {
    setZoomLevel(1);
    setNewValueOnStageMove({ x: 0, y: 0 });
    setUiState((prev) => ({
      ...prev,
      hoverState: { ...prev.hoverState, visible: false },
      selectedWhileHovered: null,
    }));

    const stage: any = stageRef?.current;
    if (stage) {
      try {
        const layers = stage?.getLayers ? stage?.getLayers() : [];
        const layer = layers?.length ? layers[0] : null;
        if (layer) {
          layer.position({ x: 0, y: 0 });
          layer?.batchDraw ? layer?.batchDraw() : layer?.draw();
        }
      } catch (err) {
        console.warn("Could not reset layer position:", err);
      }
    }
  };

  const handleFullScreenClose = (currentState: boolean) => {
    if (!currentState) {
      setZoomLevel(1);
      const totalWidthElement = document.getElementById("totalWidth");
      setContainerDimensions({
        height: 530,
        width: totalWidthElement?.offsetWidth
          ? totalWidthElement?.offsetWidth - 100
          : 942,
      });

      setUiState((prev) => ({ ...prev, selectedWhileHovered: null }));
    }
  };

  const onClickCompareButton = () => {
    if (selectedAssetList?.length > 0) {
      const payload = {
        assetCodes: selectedAssetList,
        date: moment(date).format("YYYY-MM-DD"),
        chartType: AnalyticsChartDataTypes.Chart,
      };

      if (activeKey === "Motion") {
        dispatch(getAnalyticMotionData(payload));
      } else if (activeKey === "Daylight") {
        dispatch(getAnalyticDaylightData(payload));
      } else if (activeKey === "LightOPStatus") {
        dispatch(getAnalyticLightOPStatusData(payload));
      }
    }

    setShowChartOnCompare(true);
    setTimeout(() => {
      try {
        chartRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch (e) {
        // ignore
      }
    }, 120);
  };

  // Handle tab change - call respective API
  const handleTabChange = (key: string) => {
    setActiveKey(key);
    if (selectedAssetList?.length > 0) {
      const payload = {
        assetCodes: selectedAssetList,
        date: moment(date).format("YYYY-MM-DD"),
        chartType: AnalyticsChartDataTypes.Chart,
      };

      if (key === "Motion") {
        dispatch(getAnalyticMotionData(payload));
      } else if (key === "Daylight") {
        dispatch(getAnalyticDaylightData(payload));
      } else if (key === "LightOPStatus") {
        dispatch(getAnalyticLightOPStatusData(payload));
      }
    }
  };

  const handlePlayPause = () => {
    setSelectedSearchedAssetCodeForHighlighting(""); // Reset the state
    if (playerState.isPlaying) {
      // Pause
      if (playerState.playbackInterval) {
        clearInterval(playerState.playbackInterval);
      }
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: false,
        playbackInterval: null,
      }));
    } else {
      // Play
      const intervalDuration = 1000 / playerState.playbackSpeed;

      const interval = setInterval(() => {
        setPlayerState((prev) => {
          if (prev.hourRange >= maxHourRange) {
            // Stop at the end
            clearInterval(interval);
            return {
              ...prev,
              isPlaying: false,
              playbackInterval: null,
              hourRange: 0,
            };
          }
          return { ...prev, hourRange: prev.hourRange + 1 };
        });
      }, intervalDuration);

      setPlayerState((prev) => ({
        ...prev,
        isPlaying: true,
        playbackInterval: interval,
      }));
    }
  };

  const handleStop = () => {
    if (playerState.playbackInterval) {
      clearInterval(playerState.playbackInterval);
    }
    setPlayerState((prev) => ({
      ...prev,
      isPlaying: false,
      playbackInterval: null,
      hourRange: 0,
    }));
  };

  const handleStepForward = () => {
    setPlayerState((prev) => ({
      ...prev,
      hourRange:
        prev.hourRange < maxHourRange ? prev.hourRange + 1 : maxHourRange,
    }));
  };

  const handleStepBackward = () => {
    setPlayerState((prev) => ({
      ...prev,
      hourRange: prev.hourRange > 0 ? prev.hourRange - 1 : 0,
    }));
  };

  const handleSpeedChange = (speed: number) => {
    // Clear existing interval if playing
    if (playerState.isPlaying && playerState.playbackInterval) {
      clearInterval(playerState.playbackInterval);
    }

    setPlayerState((prev) => ({
      ...prev,
      playbackSpeed: speed,
      ...(playerState.isPlaying && { playbackInterval: null }),
    }));

    // If currently playing, restart the interval with new speed
    if (playerState.isPlaying) {
      const intervalDuration = 1000 / speed;

      const interval = setInterval(() => {
        setPlayerState((prev) => {
          if (prev.hourRange >= maxHourRange) {
            clearInterval(interval);
            return {
              ...prev,
              isPlaying: false,
              playbackInterval: null,
              hourRange: 0,
            };
          }
          return { ...prev, hourRange: prev.hourRange + 1 };
        });
      }, intervalDuration);

      setPlayerState((prev) => ({
        ...prev,
        playbackInterval: interval,
      }));
    }
  };

  const handleHourRangeChange = (hour: number) => {
    // If playing, stop playback
    if (playerState.isPlaying && playerState.playbackInterval) {
      clearInterval(playerState.playbackInterval);
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: false,
        playbackInterval: null,
      }));
    }

    setPlayerState((prev) => ({ ...prev, hourRange: hour }));
  };

  // Cleanup effects
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".layer-dropdown") &&
        !target.closest(".fa-layer-group")
      ) {
        setShowLayerDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (playerState.playbackInterval) {
        clearInterval(playerState.playbackInterval);
      }
    };
  }, [playerState.playbackInterval]); // Add dependency

  /* Images */
  const file2 = "image1.png";
  const gatewayFileInitial: any = useMemo(
    () => dataUrlToFile(gatewayInitial, file2),
    [],
  );

  const file4 = "image4.png";
  const photocellFileOffline: any = useMemo(
    () => dataUrlToFile(photocellOffline, file4),
    [],
  );

  const filename10 = "image10.png";
  const selectedAssetFile: any = useMemo(
    () => dataUrlToFile(circleSelectedLight, filename10),
    [],
  );

  const [gatewayImageFileInitial, setGatewayImageFileInitial] =
    useState<any>(undefined);
  const [photocellImageFileOffline, setPhotocellImageFileOffline] =
    useState<any>(undefined);
  const [selectedAssetImageFile, setSelectedAssetImageFile] =
    useState<any>(undefined);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileInitial);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileInitial(imageElement);
  }, [gatewayFileInitial]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(selectedAssetFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setSelectedAssetImageFile(imageElement);
  }, [selectedAssetFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOffline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOffline(imageElement);
  }, [photocellFileOffline]);

  const [hoverState, setHoverState] = useState({
    visible: false,
    x: 0,
    y: 0,
  });

  /* clean up functions */
  useEffect(() => {
    return () => {
      setSelectedSearchedAssetCodeForHighlighting(""); // Reset the state
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".layer-dropdown") &&
        !target.closest(".fa-layer-group")
      ) {
        setShowLayerDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div>
      {!zoneSelectionMode && (
        <AnalyticsHeaderComponent
          viewType={selectedViewType}
          onViewTypeChange={onChangeViewType}
          date={date}
          onDateChange={handleDateChange}
          minDate={moment().subtract(13, "months").format("YYYY-MM-DD")}
          maxDate={moment().format("YYYY-MM-DD")}
          selectedAssetCount={selectedAssetList?.length}
          isSelectedListVisible={isSelectedAssetListVisible}
          onToggleSelectedList={() =>
            setIsSelectedAssetListVisible((prev) => !prev)
          }
          onCompare={onClickCompareButton}
          maxHourRange={maxHourRange}
          hourRange={playerState.hourRange}
          onHourRangeChange={handleHourRangeChange}
          isPlaying={playerState.isPlaying}
          showPlayerControls={playerState.showPlayerControls}
          onTogglePlayerControls={() =>
            setPlayerState((prev) => ({
              ...prev,
              showPlayerControls: !prev.showPlayerControls,
            }))
          }
          playbackSpeed={playerState.playbackSpeed}
          onNavigateBack={() =>
            navigate(location?.pathname?.replace("/floor-analytics", ""))
          }
          onSearchAssetSelect={setSelectedSearchedAssetCodeForHighlighting}
          disabledCompare={selectedAssetList?.length === 0}
        />
      )}

      {zoneSelectionMode && (
        <ZoneFloorplanSelection
          floorAssets={(floorAssetsAndShapes?.data || []) as FloorShape[]}
          floorAssetsLoaded={!isLoadingFloorAssetsAndShapes && floorAssetsAndShapes !== undefined}
          assignedAssets={selectionState.assignedAssets || []}
          selectedAssetCodes={selectedAssetList}
          onSelectionChange={setSelectedAssetList}
          onAssetHover={setSelectedSearchedAssetCodeForHighlighting}
          onAssetHoverEnd={() => setSelectedSearchedAssetCodeForHighlighting("")}
        />
      )}

      {/* floor plan */}
      <div className="w-100" id="totalWidth" style={{ marginBottom: "1rem" }}>
        <SkeletonComponent
          wrapperHeight="570px"
          skeletonHeight="570px"
          isLoading={
            isLoadingFloorDetails ||
            isProcessingImage ||
            isLoadingHeatmapMotionData ||
            isLoadingLightOPStatusViewData
          }
        >
          {selectedFile ? (
            <FullScreen handle={handle} onChange={handleFullScreenClose}>
              <div className="position-relative w-100">
                {/* analytics view */}
                <FloorplanCanvasComponent
                  containerDimensions={containerDimensions}
                  zoomLevel={zoomLevel}
                  onWheel={handleStageWheel}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onDragEnd={handleStageDrag}
                  selectedFile={selectedFile}
                  isLoading={isLoadingFloorAssetsAndShapes}
                  shapes={scaledShapes}
                  showLabels={showLabels}
                  selectedViewType={selectedViewType}
                  selectedAssetList={selectedAssetList}
                  selectedAssetOrder={selectedAssetOrder}
                  selectedSearchedAssetCodeForHighlighting={
                    selectedSearchedAssetCodeForHighlighting
                  }
                  gatewayImageFile={gatewayImageFileInitial}
                  photocellImageFileOffline={photocellImageFileOffline}
                  selectedAssetImageFile={selectedAssetImageFile}
                  labelLayouts={labelLayouts}
                  dynamicValues={dynamicValues}
                  labelDynamics={labelDynamics}
                  heatmapMotionDataMap={heatmapMotionDataMap}
                  lightOPStatusDataMap={lightOPStatusDataMap}
                  lightIntensityDataMap={lightIntensityDataMap}
                  hourRange={playerState.hourRange}
                  getHeatmapColor={getHeatmapColor}
                  // getLightOPStatusColor={getLightOPStatusColor}
                  onAssetClick={handleOnAssetClick}
                  onAssetMouseEnter={onAssetMouseEnter}
                  onAssetMouseLeave={onAssetMouseLeave}
                  onClickCanvas={() =>
                    setSelectedSearchedAssetCodeForHighlighting("")
                  }
                  stageRef={stageRef}
                />

                {/* The Sticky Note Sidebar */}
                {!zoneSelectionMode && (
                  <StickyNoteComponent
                    selectedAssets={selectedAssetList}
                    selectedAssetOrder={selectedAssetOrder}
                    isVisible={isSelectedAssetListVisible}
                    position={uiState.stickyNotePosition}
                    onPositionChange={(position) =>
                      setUiState((prev) => ({
                        ...prev,
                        stickyNotePosition: position,
                      }))
                    }
                    onRemoveAsset={(assetCode) => {
                      setSelectedAssetList((prev) =>
                        prev.filter((a) => a !== assetCode),
                      );
                      setSelectedSearchedAssetCodeForHighlighting("");
                    }}
                    onClearAll={() => {
                      setSelectedAssetList([]);
                      setSelectedSearchedAssetCodeForHighlighting("");
                    }}
                    onToggleVisibility={() =>
                      setIsSelectedAssetListVisible((prev) => !prev)
                    }
                    onAssetHover={setSelectedSearchedAssetCodeForHighlighting}
                    onAssetHoverEnd={() =>
                      setSelectedSearchedAssetCodeForHighlighting("")
                    }
                  />
                )}

                {/* Player Controls Panel */}
                {(selectedViewType ===
                  FloorAnalyticsView.PLAYBACK_VIEW_MOTION ||
                  selectedViewType ===
                  FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS) && (
                    <HeatmapPlayerComponent
                      playerTitle={playerTitle}
                      maxHourRange={maxHourRange}
                      hourRange={playerState.hourRange}
                      isPlaying={playerState.isPlaying}
                      playbackSpeed={playerState.playbackSpeed}
                      showControls={playerState.showPlayerControls}
                      position={playerState.playerPosition}
                      onHourRangeChange={handleHourRangeChange}
                      onPlayPause={handlePlayPause}
                      onStop={handleStop}
                      onStepForward={handleStepForward}
                      onStepBackward={handleStepBackward}
                      onSpeedChange={handleSpeedChange}
                      onPositionChange={(position) =>
                        setPlayerState((prev) => ({
                          ...prev,
                          playerPosition: position,
                        }))
                      }
                      onToggleVisibility={() =>
                        setPlayerState((prev) => ({
                          ...prev,
                          showPlayerControls: !prev.showPlayerControls,
                        }))
                      }
                    />
                  )}

                {/* Right Bottom icons */}
                <CanvasControlsComponent
                  zoomLevel={zoomLevel}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onResetZoom={handleResetZoom}
                  showLabels={showLabels}
                  onToggleLabels={setShowLabels}
                  preventLabelOverlap={preventLabelOverlap}
                  onToggleLabelOverlap={setPreventLabelOverlap}
                  showLayerDropdown={showLayerDropdown}
                  onToggleLayerDropdown={() =>
                    setShowLayerDropdown((prev) => !prev)
                  }
                  isFullscreen={handle.active}
                  onToggleFullscreen={handle.enter}
                  onExitFullscreen={handle.exit}
                  setZoomLevel={setZoomLevel}
                  setContainerDimensions={setContainerDimensions}
                // setGlobalSelectedShape={(s: any) => {
                //   setUiState((prev) => ({ ...prev, selectedWhileHovered: s }));
                //   if (!s) setSelectedSearchedAssetCodeForHighlighting("");
                // }}
                />

                {/* Mouse hover effect on the assets */}
                <HoverTooltipComponent
                  isVisible={uiState.hovered && uiState.hoverState.visible}
                  position={uiState.hoverState}
                  shape={uiState.selectedWhileHovered}
                  viewType={selectedViewType}
                  heatmapMotionInfo={
                    selectedViewType ===
                      FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
                      uiState.selectedWhileHovered
                      ? {
                        count:
                          heatmapMotionDataMap.get(
                            uiState.selectedWhileHovered.assetCode,
                          )?.[playerState.hourRange] ?? null,
                        sgHour: playerState.hourRange,
                      }
                      : undefined
                  }
                  lightOPStatusInfo={
                    selectedViewType ===
                      FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS &&
                      uiState.selectedWhileHovered
                      ? (() => {
                        let status;
                        const statusInfo = lightOPStatusDataMap?.get(
                          uiState.selectedWhileHovered.assetCode,
                        )?.[playerState.hourRange];

                        if (statusInfo === 1) {
                          status = "On";
                        } else if (statusInfo === 0) {
                          status = "Off";
                        } else {
                          status = null;
                        }

                        return {
                          status: status ?? null,
                          sgHour: playerState.hourRange,
                        };
                      })()
                      : undefined
                  }
                />
              </div>
            </FullScreen>
          ) : (
            <div className="no-preview-container">
              <h5>No Floor Plan to Preview</h5>
            </div>
          )}
        </SkeletonComponent>
      </div>

      <HeatmapLegendComponent
        isVisible={selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION}
      />

      <LightOpStatusLegendComponent
        isVisible={
          selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS
        }
      />

      <AnalyticsChartComponent
        showChart={!zoneSelectionMode && showChartOnCompare}
        selectedAssetCodes={selectedAssetList}
        motionData={motionAnalyticsData}
        lightOPStatusData={lightOPStatusAnalyticsData}
        daylightData={daylightAnalyticsData}
        isLoadingMotion={isLoadingMotionVsTimeAnalytics}
        isLoadingDaylight={isLoadingDaylightAnalytics}
        isLoadingLightOPStatus={isLoadingLightOPStatusAnalytics}
        activeKey={activeKey}
        onTabChange={handleTabChange}
        renderKey={render}
        chartRef={chartRef}
        selectedDate={date}
      />
    </div>
  );
};

export default AnalyticsComponent;
